import type {SvgIconComponent} from "@mui/icons-material";
import NotesIcon from "@mui/icons-material/Notes";
import SmsFailedOutlinedIcon from "@mui/icons-material/SmsFailedOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import {alpha, useTheme} from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type {
    AggregationSurveySearchMatch,
    AggregationSurveySubmissionListItem
} from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {SurveyAnswerSentiment, SurveyAttentionType} from "crqlar-react-common/dist/models/survey/surveySubmission.model";
import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {ElementChip} from "../../common/uiElements/base/ElementChip";
import {formatSurveyScore} from "../../common/uiElements/SurveyScoreElement";
import {
    type SurveyAnswerContext,
    SURVEY_ANSWER_PART_SEPARATOR,
    surveyAnswerParts,
    useSurveyAnswerContext
} from "../shared/surveyAnswer.util";
import {surveyMarkdownToPlainText} from "../shared/surveyMarkdownText.util";

export const SURVEY_ATTENTION_TYPE_LABEL_KEYS: Record<SurveyAttentionType, string> = {
    [SurveyAttentionType.LOW_SCORE]: 'Low score',
    [SurveyAttentionType.NEGATIVE_COMMENT]: 'Negative comment',
};

// Each chip's rule, shown in its tooltip.
const ATTENTION_TYPE_HINT_KEYS: Record<SurveyAttentionType, string> = {
    [SurveyAttentionType.LOW_SCORE]: 'The reply scores under 50.',
    [SurveyAttentionType.NEGATIVE_COMMENT]: "One of the reply's answers reads negative.",
};

const ATTENTION_TYPE_ICONS: Record<SurveyAttentionType, SvgIconComponent> = {
    [SurveyAttentionType.LOW_SCORE]: ThumbDownOutlinedIcon,
    [SurveyAttentionType.NEGATIVE_COMMENT]: SmsFailedOutlinedIcon,
};

// The enum's order, whatever order the API sends them in.
const ATTENTION_TYPE_ORDER = Object.values(SurveyAttentionType);

const QUOTE_MAX_LENGTH = 80;

// Shorter than a quote: the rating follows the question on the same line.
const QUESTION_MAX_LENGTH = 48;

const SURVEY_DOT_SIZE = 10;

/** Why a reply needs a look, with the rule in its tooltip. */
function SurveyAttentionTypeChip({type}: {type: SurveyAttentionType}) {
    const {t} = useTranslation();
    const theme = useTheme();

    const Icon = ATTENTION_TYPE_ICONS[type];

    return (
        <Tooltip title={t(ATTENTION_TYPE_HINT_KEYS[type])}>
            <Box
                component={'span'}
                sx={{display: 'inline-flex', maxWidth: '100%', '& [data-element-chip-label]': {color: 'error.dark'}}}
            >
                <ElementChip
                    text={t(SURVEY_ATTENTION_TYPE_LABEL_KEYS[type])}
                    startIcon={<Icon sx={{fontSize: 16, color: 'error.dark'}}/>}
                    borderColor={alpha(theme.palette.error.main, 0.34)}
                    backgroundColor={alpha(theme.palette.error.main, 0.08)}
                />
            </Box>
        </Tooltip>
    );
}

/** Every reason a reply needs a look - both when both apply - side by side, wrapping when the column is narrow. */
export function SurveyAttentionTypeChips({types}: {types: SurveyAttentionType[] | null | undefined}) {
    const shown = ATTENTION_TYPE_ORDER.filter(type => types?.includes(type));
    if (!shown.length) return null;

    return (
        <Stack direction={'row'} flexWrap={'wrap'} alignItems={'center'} gap={0.75} sx={{minWidth: 0, py: 0.5}}>
            {shown.map(type => <SurveyAttentionTypeChip key={type} type={type}/>)}
        </Stack>
    );
}

// By code point, so an emoji at the cut is not split in half.
const cutText = (text: string, maxLength: number) => {
    const characters = Array.from(text);
    return characters.length > maxLength
        ? `${characters.slice(0, maxLength).join('').trimEnd()}…`
        : text;
};

/*
    Only the guest's own words are quoted: the sentiment was read from them, and without the question an
    option would print as its id. An answer with a blacked-out part is passed over; a sensitive one never
    gets here with a sentiment, because the API withholds the whole answer.
*/
const negativeQuote = (submission: AggregationSurveySubmissionListItem, ctx: SurveyAnswerContext) => {
    const blackedOut = new Set(submission.redactionMarks?.map(mark => mark.questionId));

    for (const answer of submission.answers ?? []) {
        if (answer.aiSentiment !== SurveyAnswerSentiment.NEGATIVE) continue;
        if (answer.questionId && blackedOut.has(answer.questionId)) continue;

        const text = surveyAnswerParts(answer, undefined, ctx).parts
            .filter(part => part.field)
            .map(part => part.text)
            .join(SURVEY_ANSWER_PART_SEPARATOR)
            .replace(/\s+/g, ' ')
            .trim();
        if (!text) continue;

        return cutText(text, QUOTE_MAX_LENGTH);
    }

    return undefined;
};

/** While searching: the answer the search found, with its question in front and the search words marked. */
function SurveySearchMatchLine({match, ctx}: {match: AggregationSurveySearchMatch, ctx: SurveyAnswerContext}) {
    const {t} = useTranslation();
    const theme = useTheme();

    const question = cutText(surveyMarkdownToPlainText(ctx.localize(match.question ?? undefined)), QUESTION_MAX_LENGTH);

    // The API places the ranges in this very text; one that does not fit it is skipped rather than guessed.
    const parts: React.ReactNode[] = [];
    let at = 0;
    match.highlights.forEach(({start, end}, index) => {
        if (start < at || end > match.text.length || end <= start) return;
        if (start > at) parts.push(match.text.slice(at, start));
        parts.push(
            <Box
                key={index}
                component={'mark'}
                sx={{bgcolor: alpha(theme.palette.warning.main, 0.28), color: 'inherit', borderRadius: 0.5}}
            >
                {match.text.slice(start, end)}
            </Box>
        );
        at = end;
    });
    if (at < match.text.length) parts.push(match.text.slice(at));

    return (
        <Typography variant={'body2'} color={'text.secondary'} sx={{overflowWrap: 'anywhere'}}>
            {question ? `${question} · ` : ''}“{parts}”{match.more > 0 ? ` ${t('+{{count}} more', {count: match.more})}` : ''}
        </Typography>
    );
}

/**
 * The guest with the reply's score, and a mark when staff left notes. Under it, while searching, the answer the
 * search found; otherwise, only on a reply that needs a look, the line that explains its chip: what the guest said
 * that reads negative - or, for a low score without one, the question they rated lowest.
 */
export function SurveySubmissionGuestCell({submission}: {submission: AggregationSurveySubmissionListItem}) {
    const {t, i18n} = useTranslation();
    const ctx = useSurveyAnswerContext();
    const language = i18n.resolvedLanguage || i18n.language;
    // No trailing zeros: a 2 reads "2", never "2.0".
    const formatNumber = useMemo(() => new Intl.NumberFormat(language, {maximumFractionDigits: 2}).format, [language]);

    const guest = submission.profileName?.trim() || t('Anonymous guest');
    const notes = submission.reviewNotes?.trim();
    const types = submission.attentionTypes ?? [];
    const match = submission.searchMatch;
    const quote = !match && types.includes(SurveyAttentionType.NEGATIVE_COMMENT) ? negativeQuote(submission, ctx) : undefined;

    // A question title is markdown with variable tokens.
    const lowest = !match && !quote && types.includes(SurveyAttentionType.LOW_SCORE) ? submission.lowestAnswer : undefined;
    const lowestQuestion = lowest
        ? cutText(surveyMarkdownToPlainText(ctx.localize(lowest.question ?? undefined)), QUESTION_MAX_LENGTH)
        : '';
    const rating = lowest && lowestQuestion
        ? lowest.max == null
            ? t('{{question}} rated {{value}}', {question: lowestQuestion, value: formatNumber(lowest.value)})
            : t('{{question}} rated {{value}} of {{max}}', {
                question: lowestQuestion,
                value: formatNumber(lowest.value),
                max: formatNumber(lowest.max)
            })
        : undefined;

    return (
        <Stack sx={{minWidth: 0, py: 0.5}}>
            <Stack direction={'row'} alignItems={'center'} gap={0.75} sx={{minWidth: 0}}>
                <Typography variant={'body1'} noWrap>
                    {submission.score == null ? guest : `${guest} · ${formatSurveyScore(submission.score)}`}
                </Typography>

                {!!notes && (
                    <Tooltip title={surveyMarkdownToPlainText(notes)}>
                        <NotesIcon sx={{fontSize: 16, color: 'text.secondary', flexShrink: 0}}/>
                    </Tooltip>
                )}
            </Stack>

            {!!match && <SurveySearchMatchLine match={match} ctx={ctx}/>}

            {!!quote && (
                <Typography variant={'body2'} color={'text.secondary'} noWrap>
                    “{quote}”
                </Typography>
            )}

            {!!rating && (
                <Typography variant={'body2'} color={'text.secondary'} noWrap>
                    {rating}
                </Typography>
            )}
        </Stack>
    );
}

export function SurveySubmissionSurveyCell({name, color}: {name: string, color: string}) {
    return (
        <Stack direction={'row'} alignItems={'center'} gap={1} sx={{minWidth: 0}}>
            <Box
                aria-hidden
                sx={{width: SURVEY_DOT_SIZE, height: SURVEY_DOT_SIZE, flexShrink: 0, borderRadius: '50%', bgcolor: color}}
            />

            <Typography variant={'body1'} noWrap>{name}</Typography>
        </Stack>
    );
}
